'use client';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Users,
  BookOpen,
  Trophy,
  Star,
  Code,
  Laptop,
  Target,
  CheckCircle,
  Play,
  Calendar,
  Award,
  TrendingUp,
  MessageSquare,
  Clock,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { Card, Button, Badge } from '../components/ui';

const features = [
  {
    icon: Code,
    title: 'Real-World Projects',
    description: 'Work on actual industry projects that make a difference and build your portfolio.'
  },
  {
    icon: Users,
    title: 'Expert Mentorship',
    description: 'Learn from experienced professionals who guide you throughout your journey.'
  },
  {
    icon: Trophy,
    title: 'Skill Development',
    description: 'Master in-demand technologies and frameworks used by top companies.'
  },
  {
    icon: Target,
    title: 'Career Growth',
    description: 'Build connections and gain experience that accelerates your career path.'
  }
];

const stats = [
  { number: '500+', label: 'Students Trained' },
  { number: '50+', label: 'Industry Projects' },
  { number: '95%', label: 'Placement Rate' },
  { number: '4.8/5', label: 'Student Rating' }
];

const testimonials = [
  {
    name: 'Sarah Johnson',
    role: 'Frontend Developer at Google',
    avatar: '/api/placeholder/64/64',
    content: 'StartupWorld gave me the real-world experience I needed. The mentorship was exceptional!',
    rating: 5
  },
  {
    name: 'Michael Chen',
    role: 'Full Stack Developer at Microsoft',
    avatar: '/api/placeholder/64/64',
    content: 'The project-based learning approach helped me land my dream job. Highly recommended!',
    rating: 5
  },
  {
    name: 'Emily Davis',
    role: 'UI/UX Designer at Apple',
    avatar: '/api/placeholder/64/64',
    content: 'Amazing program with great mentors. I learned more here than in my entire college course.',
    rating: 5
  }
];

const programs = [
  {
    title: 'Web Development',
    duration: '3 Months',
    level: 'Beginner to Advanced',
    technologies: ['React', 'Node.js', 'MongoDB', 'AWS'],
    color: 'from-blue-500 to-purple-600'
  },
  {
    title: 'Mobile Development',
    duration: '4 Months',
    level: 'Intermediate',
    technologies: ['React Native', 'Flutter', 'Firebase', 'APIs'],
    color: 'from-green-500 to-teal-600'
  },
  {
    title: 'Data Science',
    duration: '4 Months',
    level: 'Advanced',
    technologies: ['Python', 'TensorFlow', 'SQL', 'Tableau'],
    color: 'from-purple-500 to-pink-600'
  }
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                StartupWorld
              </h1>
            </div>
          </div>

          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Features</a>
              <a href="#programs" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Programs</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">Reviews</a>

              {/* Register Dropdown */}
              <div className="relative">
                <button
                  onClick={() => { setIsRegisterOpen(!isRegisterOpen); setIsLoginOpen(false); }}
                  className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium focus:outline-none"
                >
                  Register
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isRegisterOpen ? "rotate-180" : ""}`} />
                </button>

                {isRegisterOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white shadow-lg border rounded-lg py-2 z-50" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs font-semibold text-gray-500 px-4 py-1">Create Your Profile</p>
                    <Link href="/student/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Student</Link>
                    <Link href="/college/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register College</Link>
                    <Link href="/teacher/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Teacher</Link>
                    <Link href="/employer/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Employer</Link>
                    <Link href="/sponsorer/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Sponsorer</Link>
                    <Link href="/mentor/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Register Mentor</Link>
                    <Link href="/winners/today" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Today Winners</Link>
                  </div>
                )}
              </div>

              {/* Login Dropdown */}
              <div className="relative">
                <button
                  onClick={() => { setIsLoginOpen(!isLoginOpen); setIsRegisterOpen(false); }}
                  className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium focus:outline-none"
                >
                  Login
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isLoginOpen ? "rotate-180" : ""}`} />
                </button>

                {isLoginOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg border rounded-lg py-2 z-50" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs font-semibold text-gray-500 px-4 py-1">Select Your Portal</p>
                    <Link href="/student/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700">Student Login</Link>
                    <Link href="/employer/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700">Employer Login</Link>
                    <Link href="/staff/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700">Staff Login</Link>
                    <Link href="/teamleader/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700">Team Leader Login</Link>
                    <hr className="my-1 border-gray-100" />
                    <Link href="/login" className="block px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50">All Logins (Unified)</Link>
                  </div>
                )}
              </div>

              <Link href="/login" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-blue-700 hover:to-purple-700">Login</Link>
            </div>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-gray-900 p-2"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-b">
            <a href="#features" className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium">Features</a>
            <a href="#programs" className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium">Programs</a>
            <a href="#testimonials" className="block text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium">Reviews</a>

            {/* Mobile Register Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setIsRegisterOpen(!isRegisterOpen); setIsLoginOpen(false); }}
                className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium focus:outline-none"
              >
                Register
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {isRegisterOpen && (
                <div className="ml-4 border-l-2 border-gray-200 pl-4 py-1 space-y-1">
                  <Link href="/student/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register Student</Link>
                  <Link href="/college/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register College</Link>
                  <Link href="/teacher/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register Teacher</Link>
                  <Link href="/employer/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register Employer</Link>
                  <Link href="/sponsorer/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register Sponsorer</Link>
                  <Link href="/mentor/register" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Register Mentor</Link>
                  <Link href="/winners/today" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Today Winners</Link>
                </div>
              )}
            </div>

            {/* Mobile Login Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setIsLoginOpen(!isLoginOpen); setIsRegisterOpen(false); }}
                className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium focus:outline-none"
              >
                Login
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {isLoginOpen && (
                <div className="ml-4 border-l-2 border-blue-200 pl-4 py-1 space-y-1">
                  <Link href="/student/login" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-blue-50 rounded">Student Login</Link>
                  <Link href="/employer/login" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-amber-50 rounded">Employer Login</Link>
                  <Link href="/staff/login" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-green-50 rounded">Staff Login</Link>
                  <Link href="/teamleader/login" className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-purple-50 rounded">Team Leader Login</Link>
                  <Link href="/login" className="block px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded">All Logins (Unified)</Link>
                </div>
              )}
            </div>

            <Link href="/login" className="block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-lg text-base font-medium text-center mx-3">Login</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => (
  <section className="pt-20 pb-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-blue-100 text-blue-800 px-4 py-2">
            🚀 India&apos;s Premier Internship Platform
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Transform Your
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {" "}Career{" "}
            </span>
            with Real-World Experience
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Join thousands of students who have accelerated their careers through hands-on projects,
            expert mentorship, and industry-relevant skills training.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link href="/student/login">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Your Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="group">
              <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              Watch Demo
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Why Choose StartupWorld?
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          We provide everything you need to succeed in today&apos;s competitive tech industry
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 h-full hover:shadow-lg transition-shadow text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <feature.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Programs = () => (
  <section id="programs" className="py-20 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Our Internship Programs
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Choose from our comprehensive programs designed to match your career goals
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {programs.map((program, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 h-full hover:shadow-lg transition-shadow">
              <div className={`w-full h-32 bg-gradient-to-br ${program.color} rounded-lg mb-6 flex items-center justify-center`}>
                <Laptop className="h-12 w-12 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">{program.title}</h3>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>{program.duration}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <span>{program.level}</span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Technologies:</h4>
                <div className="flex flex-wrap gap-2">
                  {program.technologies.map((tech, techIndex) => (
                    <Badge key={techIndex} variant="outline" className="text-xs">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>

              <Button className="w-full">
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section id="testimonials" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          What Our Students Say
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Hear from students who have transformed their careers with StartupWorld
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 h-full">
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                ))}
              </div>

              <p className="text-gray-600 mb-6 italic">&ldquo;{testimonial.content}&rdquo;</p>

              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-semibold text-sm">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Ready to Start Your Journey?
        </h2>
        <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
          Join thousands of students who have transformed their careers.
          Take the first step towards your dream job today.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link href="/student/login">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
            <MessageSquare className="mr-2 h-5 w-5" />
            Contact Us
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-gray-900 text-white py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="mb-8 md:mb-0">
          <h3 className="text-2xl font-bold mb-4">StartupWorld</h3>
          <p className="text-gray-400 mb-4">
            Empowering the next generation of tech professionals through hands-on learning and industry experience.
          </p>
          <div className="flex space-x-4">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">f</span>
            </div>
            <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">t</span>
            </div>
            <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">in</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-4">Programs</h4>
          <ul className="space-y-2 text-gray-400">
            <li><a href="#" className="hover:text-white">Web Development</a></li>
            <li><a href="#" className="hover:text-white">Mobile Development</a></li>
            <li><a href="#" className="hover:text-white">Data Science</a></li>
            <li><a href="#" className="hover:text-white">UI/UX Design</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-gray-400">
            <li><a href="#" className="hover:text-white">About Us</a></li>
            <li><a href="#" className="hover:text-white">Careers</a></li>
            <li><a href="#" className="hover:text-white">Blog</a></li>
            <li><a href="#" className="hover:text-white">Contact</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-4">Support</h4>
          <ul className="space-y-2 text-gray-400">
            <li><a href="#" className="hover:text-white">Help Center</a></li>
            <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
            <li><a href="#" className="hover:text-white">Terms of Service</a></li>
            <li><a href="#" className="hover:text-white">Student Portal</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
        <p>&copy; 2024 StartupWorld. All rights reserved. Empowering careers through innovation.</p>
      </div>
    </div>
      </footer>
);

export default function Home() {
  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <Features />
      <Programs />
      <Testimonials />
      <CTA />
      <Footer />
    </div>
  );
}
